import axios, { type AxiosError } from 'axios'
import resetStore from '@/components/use/resetStore'
import Honeybadger from '@honeybadger-io/js'
import { honeyBadger } from '@/config/honeyBadger'
import RecoverableException from '@/exceptions/RecoverableException'
import { useNativeScanStore } from '@/storage/nativeScan'
import config from '@/config'
import redactRequestError from '@/utilities/redactRequestError'

const errorInterceptor = (): void => {
  axios.interceptors.request.use(
    (config) => config,
    (error) => {
      console.error('request error', error.toJSON())

      return Promise.reject(error)
    },
  )

  axios.interceptors.response.use(
    (response) => response,
    (
      error: AxiosError<{
        errors:
          | { code: number; message: string }[]
          | {
              [key: string]: string[]
            }
        source?: 'capture' | 'data'
      }>,
    ) => {
      console.error('Error:', error.code, error?.response)
      if (error.isAxiosError) {
        if (error.response?.status === 422) {
          return Promise.reject(error)
        }

        if (error?.response?.status === 401) {
          const uuid = useNativeScanStore().uuid

          resetStore()

          const url = new URL(config.unauthorized.redirect.url)
          if (uuid.length > 0) {
            url.searchParams.set('uuid', uuid)
          }
          window.location.replace(url.toString())
          return
        }

        // A refused document content read is reported by the caller, which knows which
        // document it was; reporting it here too filed every one twice.
        if (error.config?.responseType === 'blob') {
          return Promise.reject(error)
        }

        if (error.response && error.response.data?.source === undefined) {
          Honeybadger.configure(honeyBadger).notify(
            new Error('Unhandled api exception'),
            'UnhandledApiException',
            {
              context: {
                error: redactRequestError(error),
              },
            },
          )
          return Promise.reject(error)
        }

        switch (error.code) {
          case 'ERR_BAD_REQUEST':
            handleErrorBadRequest(
              error.status,
              error.response?.data?.errors[0]?.message,
              error.response?.data?.errors[0]?.code,
            )
            break
          case 'ERR_FR_TOO_MANY_REDIRECTS':
            throw new RecoverableException(
              'Too many redirects, request aborted.',
            )
          case 'ERR_BAD_OPTION_VALUE':
          case 'ERR_BAD_OPTION':
            throw new RecoverableException(
              'Invalid pre-request, please try again.',
            )
          case 'ERR_NETWORK':
            throw new RecoverableException(
              'Network error, please check your connection and try again.',
            )
          case 'ERR_BAD_RESPONSE':
            handleErrorBadResponse(
              error.response?.status,
              error.response?.data?.errors[0]?.message,
              error.response?.data?.errors[0]?.code,
            )
            break
          case 'ECONNABORTED':
            throw new RecoverableException(
              'The request was aborted. Please check your network and try again.',
            )
          case 'ETIMEDOUT':
            throw new RecoverableException(
              'The request timed out. Please check your network and try again.',
            )
          case 'ERR_DEPRECATED':
          case 'ERR_CANCELLED':
          default:
            break
        }
      }

      return Promise.reject(error)
    },
  )
}

export default errorInterceptor

const handleErrorBadRequest = (
  statusCode: number | undefined,
  message: string | undefined,
  code: number | undefined,
): void => {
  if (!statusCode) {
    throw new RecoverableException(message || 'An error occurred')
  }
  const codeText = code === 0 ? '' : `[${code}] `
  switch (statusCode) {
    case 400:
      message = `${codeText} ${message || 'Bad request'}`
      break
    case 403:
      message = `${codeText} ${message || 'Forbidden'}`
      break
    case 404:
      message = `${codeText} ${message || 'Resource not found'}`
      break
    default:
      break
  }
  throw new RecoverableException(message || 'An error occurred')
}

const handleErrorBadResponse = (
  statusCode: number | undefined,
  message: string | undefined,
  code: number | undefined,
): void => {
  if (!statusCode) {
    return
  }
  const codeText = code === 0 ? '' : `[${code}] `
  switch (statusCode) {
    case 503:
      message = `${codeText} ${message || 'Service unavailable'}`
      break
    default:
      break
  }
  throw new RecoverableException(message || 'An error occurred')
}
